/**
 * Per-CEFR-band mastery breakdown — one row per band (A1, A2, B1, ...).
 *
 * Bands aren't independent: if you're at 75% on A2, you can't honestly
 * be at 90% on B1 because B1 builds on A2. The previous version showed
 * raw `avg memory across touched skills in the band` per row, which
 * disagreed with that intuition — sparse B1 coverage of foundational
 * skills could read higher than dense A2 coverage of hard ones.
 *
 * The metric averaged was `retrievability` — FSRS's "can I recall it
 * right now" — which is near 1 immediately after practice regardless
 * of how well the skill is actually known. That produced the 2026-06-04
 * "I am obviously not 97% in B1" reading: practiced-recently ≠ known.
 *
 * The bar now averages `masteryProbability` — the IRT belief's
 * answer to "P(P(correct at b=0) ≥ 0.85)". It's the mathematically
 * correct "have I learned this?" metric, where retrievability is the
 * "have I forgotten this?" metric. Both come from /skills/mastery.
 *
 * Bayesian-flavoured cascade still applies: each band's posterior is
 * a coverage-weighted blend of (this-band evidence) and (a prior
 * shifted down from the lower band by a structural step). With zero
 * coverage, you inherit the lower band minus the step; with full
 * coverage, you trust your local evidence. Math:
 *
 *   prior[band]  = max(0, adj[band-1] - STEP)        # bands get harder
 *   adj[band]    = c * raw[band] + (1 - c) * prior   # c = coverage
 *
 * STEP is the empirical difficulty step between consecutive CEFR
 * bands on the mastery-probability scale. 0.08 was picked by hand —
 * tune if it reads too pessimistic / too optimistic.
 *
 * Computed client-side from the same /skills/mastery payload; no new
 * backend call. The bar fill shows the calibrated `adj`; the row
 * label still surfaces the raw `avg mastery` and a tooltip explains
 * where the two differ so the math is auditable.
 */

import { cefrText, masteryBar } from "../lib/palette";

type SkillRow = {
  id: number;
  code: string;
  cefrLevel?: string | null;
  nAttempts: number;
  retrievability: number;
  masteryProbability: number;
};

const BANDS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
const MASTER_THRESHOLD = 0.85;
// Inter-band difficulty step. The prior for band N is centred at
// (adj[N-1] - STEP) — i.e. each successive band is structurally that
// much harder, before any local evidence is considered.
const STEP = 0.08;

export function CefrBandBreakdown({ skills }: { skills: readonly SkillRow[] }) {
  // Group skills by CEFR band. Skills with no CEFR level (e.g.
  // foundational meta-skills) get lumped into "Unleveled" at the
  // bottom — there's no good reason to drop them.
  const byBand = new Map<string, SkillRow[]>();
  for (const s of skills) {
    const band = s.cefrLevel ?? "—";
    const bucket = byBand.get(band) ?? [];
    bucket.push(s);
    byBand.set(band, bucket);
  }

  // Render in CEFR order, then any unleveled bucket last.
  const orderedBands = [
    ...BANDS.filter((b) => byBand.has(b)),
    ...[...byBand.keys()].filter(
      (b) => !BANDS.includes(b as (typeof BANDS)[number]),
    ),
  ];

  // Cascade pass: walk CEFR bands in order computing the calibrated
  // posterior `adj` per band. Unleveled bands don't participate — they
  // get raw values and don't feed forward.
  type BandStats = {
    band: string;
    bucket: SkillRow[];
    total: number;
    touched: SkillRow[];
    coverage: number; // 0..1
    rawP: number; // 0..1
    adjP: number; // 0..1, the Bayesian-cascade-adjusted posterior
    isCascade: boolean;
  };
  const stats: BandStats[] = [];
  let prevAdj: number | null = null;
  for (const band of orderedBands) {
    const bucket = byBand.get(band) ?? [];
    const total = bucket.length;
    const touched = bucket.filter((s) => s.nAttempts > 0);
    const c = total === 0 ? 0 : touched.length / total;
    // Average mastery probability across skills the user has actually
    // attempted in this band. Was retrievability — that read "right now
    // can I recall it" and ran near 1 post-practice regardless of true
    // skill, which is what surfaced the 6/04 inflated B1 number.
    const rawP =
      touched.length === 0
        ? 0
        : touched.reduce((sum, s) => sum + s.masteryProbability, 0) /
          touched.length;
    const inCascade = BANDS.includes(band as (typeof BANDS)[number]);
    let adjP: number;
    if (!inCascade) {
      // Unleveled buckets don't participate in the chain.
      adjP = rawP;
    } else if (prevAdj === null) {
      // First (lowest) band — no prior, evidence is itself.
      adjP = rawP;
    } else {
      const prior = Math.max(0, prevAdj - STEP);
      adjP = c === 0 ? prior : c * rawP + (1 - c) * prior;
    }
    if (inCascade) prevAdj = adjP;
    stats.push({
      band,
      bucket,
      total,
      touched,
      coverage: c,
      rawP,
      adjP,
      isCascade: inCascade,
    });
  }

  return (
    <section className="mb-8 sm:mb-10 border border-ink-100 bg-paper">
      <div className="px-4 py-3 border-b border-ink-100">
        <h2 className="font-serif text-base text-ink-900">By CEFR level</h2>
        <p className="text-xs text-ink-400 mt-0.5 leading-snug">
          Bar shows the <strong>calibrated</strong> estimate — a
          coverage-weighted blend of this band's evidence and a prior carried up
          from the lower band (CEFR bands compound, so weak A2 holds B1 back
          even when you've only touched a few B1 items). Sparse coverage = leans
          on the prior; full coverage = trusts local evidence.
        </p>
      </div>
      <ul className="divide-y divide-ink-100">
        {stats.map((s) => {
          const mastered = s.touched.filter(
            (r) => r.retrievability >= MASTER_THRESHOLD,
          );
          const rawPct = Math.round(s.rawP * 100);
          const adjPct = Math.round(s.adjP * 100);
          const coverPct = Math.round(s.coverage * 100);
          const fill =
            s.touched.length === 0 && coverPct === 0
              ? "bg-transparent"
              : masteryBar(s.adjP);
          const showRaw =
            s.touched.length > 0 && Math.abs(rawPct - adjPct) >= 2;
          return (
            <li
              key={s.band}
              className="px-4 py-3"
              title={`${s.band}: ${s.touched.length} of ${s.total} skills practiced (${coverPct}% touched). Local avg memory ${rawPct}%; calibrated against the lower band to ${adjPct}%.`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className={`font-serif text-lg font-semibold tabular-nums ${cefrText(s.band)}`}
                  >
                    {s.band}
                  </span>
                  <span className="text-xs text-ink-400">
                    {s.touched.length} of {s.total} skills practiced
                    {mastered.length > 0 && (
                      <span
                        className="text-emerald-700"
                        title="Practiced skills where memory currently reads ≥ 85%. Not the same as the aggregate card's 'mastered' (which uses the stricter IRT-belief criterion)."
                      >
                        {" "}
                        · {mastered.length} at 85%+
                      </span>
                    )}
                  </span>
                </div>
                <div className="text-xs text-ink-400 tabular-nums">
                  {s.touched.length === 0 && coverPct === 0 ? (
                    s.isCascade && stats[0] !== s ? (
                      <span title="No evidence in this band yet — the calibrated estimate is the prior carried up from the lower band, minus the inter-band step.">
                        carried prior {adjPct}%
                      </span>
                    ) : (
                      "not started"
                    )
                  ) : (
                    <>
                      memory {adjPct}%
                      {showRaw && (
                        <span
                          className="text-ink-300"
                          title={`Raw local average across touched skills = ${rawPct}%. Calibrated to ${adjPct}% by blending with the lower-band prior.`}
                        >
                          {" "}
                          (raw {rawPct}%)
                        </span>
                      )}{" "}
                      · {coverPct}% touched
                    </>
                  )}
                </div>
              </div>
              {/* Three-layer bar:
                  - grey TRACK = the band itself (100%)
                  - ink-200 COVERAGE = % of band touched
                  - coloured FILL = calibrated estimate (adjP), scaled
                    so a fully-touched band at adjP=80% shows 80% fill,
                    and a thinly-covered band at adjP=80% with 20%
                    coverage shows 16% (i.e. the bar both reflects
                    confidence-in-estimate AND the estimate itself). */}
              <div className="relative h-2 w-full bg-ink-100 overflow-hidden">
                <div
                  className="absolute inset-y-0 bg-ink-200"
                  style={{ width: `${coverPct}%` }}
                />
                <div
                  className={`absolute inset-y-0 ${fill}`}
                  style={{ width: `${Math.round(s.adjP * coverPct)}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
